import { useEffect, useState } from 'react'
import { admin } from '../api/client'
import type { NotificationEscalation as Escalation } from '../api/types'
import {
  type EscalationDraft, MAX_ESCALATION_HOURS, draftOf, escalationBody, escalationProblem, toggled,
} from '../lib/escalation'
import { useAsync } from '../lib/useAsync'

const message = (failed: unknown) => failed instanceof Error ? failed.message : 'Something went wrong.'

type Step = 'second' | 'third'
const STEPS: { key: Step; label: string }[] = [
  { key: 'second', label: 'Second notice' },
  { key: 'third', label: 'Third notice' },
]

/** Escalation of unacknowledged alarms (spec section 8; platform admins). An
 *  alarm nobody has acknowledged is sent again, to the people chosen here: a
 *  second notice, then a third. Only a check with a note on the alarm
 *  acknowledges it; a bare check does not. Off until switched on. */
export function NotificationEscalation({ people }: { people: { id: number; name: string }[] }) {
  const loaded = useAsync<Escalation>(() => admin.escalation(), [])
  const [draft, setDraft] = useState<EscalationDraft | null>(null)
  const [state, setState] = useState<{ busy: boolean; note?: string; error?: string }>({ busy: false })
  useEffect(() => { if (loaded.data && !draft) setDraft(draftOf(loaded.data)) }, [loaded.data, draft])

  if (loaded.error && !draft) {
    return <section className="al-escalation" aria-labelledby="al-escalation-head">
      <h3 id="al-escalation-head">Escalation</h3>
      <p className="error" role="alert">{loaded.error}</p>
    </section>
  }
  if (!draft) {
    return <section className="al-escalation" aria-labelledby="al-escalation-head">
      <h3 id="al-escalation-head">Escalation</h3><p className="muted">Loading…</p>
    </section>
  }

  const problem = escalationProblem(draft)
  const edit = (next: EscalationDraft) => { setDraft(next); setState({ busy: false }) }
  const save = async () => {
    const body = escalationBody(draft)
    if (!body) return
    setState({ busy: true })
    try {
      setDraft(draftOf(await admin.setEscalation(body)))
      setState({ busy: false, note: 'Escalation saved.' })
    } catch (failure) {
      setState({ busy: false, error: message(failure) })
    }
  }

  return (
    <section className="al-escalation" aria-labelledby="al-escalation-head">
      <h3 id="al-escalation-head">Escalation</h3>
      <p className="muted">
        An alarm nobody has acknowledged is sent again to the people chosen here: a second notice,
        then a third. Only a check with a note acknowledges an alarm for everyone, and that stops
        the notices; a check without a note does not. Warnings are never escalated, and these
        emails are sent whatever the rule sends and whatever a person receives otherwise. They
        count from when the event became an alarm.
      </p>
      <p className="muted small">
        When this is switched on, every alarm already past its hours is sent at once, within a
        minute: both notices to anyone on both lists.
      </p>
      <label className="al-escalation-on">
        <input type="checkbox" className="switch" checked={draft.enabled}
               onChange={() => edit({ ...draft, enabled: !draft.enabled })} />
        Send notices for alarms nobody has acknowledged
      </label>
      {STEPS.map(({ key, label }) => (
        <fieldset key={key} className="al-escalation-step" disabled={!draft.enabled}>
          <legend>{label}</legend>
          <label>
            After{' '}
            <input type="number" min={1} max={MAX_ESCALATION_HOURS} step={1} inputMode="numeric"
                   aria-label={`${label}: hours without an acknowledgement`}
                   value={draft[key].after_h}
                   onChange={(e) => edit({ ...draft, [key]: { ...draft[key], after_h: e.target.value } })} />
            {' '}hours without an acknowledgement, send to
          </label>
          <div className="al-escalation-people">
            {people.map((p) => (
              <label key={p.id}>
                <input type="checkbox" checked={draft[key].people.includes(p.id)}
                       onChange={() => edit({ ...draft, [key]: { ...draft[key], people: toggled(draft[key].people, p.id) } })} />
                {p.name}
              </label>
            ))}
          </div>
        </fieldset>
      ))}
      <p className="muted small">
        A person chosen here who is not a member of a strategy's project still receives the email
        and can acknowledge the alarm from their inbox, the bell at the top of the page (Mark
        checked, with what is being done), but the email's link to the strategy answers not found
        for them. Someone who cannot sign in, or has email off, is
        not sent a notice.
      </p>
      {problem && <p className="error" role="alert">{problem}</p>}
      <div className="al-escalation-actions">
        <button type="button" className="button primary" disabled={state.busy || !!problem}
                onClick={() => void save()}>{state.busy ? 'Saving…' : 'Save escalation'}</button>
        {state.note && <span className="muted" role="status">{state.note}</span>}
        {state.error && <span className="error" role="alert">{state.error}</span>}
      </div>
    </section>
  )
}
